import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SweetAlertOptions } from 'sweetalert2';

/** State of the Client Settings slide-out panel (EN-3503). */
export interface ClientSettingsPanelState {
	open: boolean;
	clientId?: string;
}

const UNSAVED_CHANGES_SWAL_BUTTONS = {
	showCancelButton: true,
	confirmButtonText: 'Discard Changes',
	cancelButtonText: 'Continue Editing',
	allowOutsideClick: false,
	customClass: {
		confirmButton: 'btn btn-danger',
		cancelButton: 'btn btn-secondary',
	},
	buttonsStyling: false,
} as const;

/** SweetAlert body listing unsaved section/form labels when closing Client Settings. */
export function buildUnsavedChangesSwalOptions(intro: string, summary: string[]): SweetAlertOptions {
	if (!summary.length) {
		return {
			title: 'Unsaved Changes',
			text: intro,
			icon: 'warning',
			...UNSAVED_CHANGES_SWAL_BUTTONS,
		};
	}

	const items = summary
		.map((item) => `<li>${item.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</li>`)
		.join('');

	return {
		title: 'Unsaved Changes',
		html: `<p>${intro}</p><ul style="text-align:left;margin:0.75rem 0 0;padding-left:1.25rem;">${items}</ul>`,
		icon: 'warning',
		...UNSAVED_CHANGES_SWAL_BUTTONS,
	};
}

/**
 * Coordinates the Client Settings slide-out panel between the sidebar (which triggers it) and the
 * drawer host mounted in the admin layout. Also lets the sidebar consult the panel's unsaved-changes
 * state before switching clients.
 */
@Injectable({ providedIn: 'root' })
export class ClientSettingsPanelService {
	private readonly stateSubject = new BehaviorSubject<ClientSettingsPanelState>({ open: false });
	readonly state$: Observable<ClientSettingsPanelState> = this.stateSubject.asObservable();

	private dirtyChecker: (() => boolean) | null = null;
	private unsavedChangesSummaryProvider: (() => string[]) | null = null;

	open(clientId: string): void {
		if (!clientId) {
			return;
		}
		this.stateSubject.next({ open: true, clientId });
	}

	close(): void {
		this.dirtyChecker = null;
		this.unsavedChangesSummaryProvider = null;
		this.stateSubject.next({ open: false });
	}

	get isOpen(): boolean {
		return this.stateSubject.value.open;
	}

	/** Registered by the drawer so other components can query whether the open panel has unsaved edits. */
	setDirtyChecker(checker: (() => boolean) | null): void {
		this.dirtyChecker = checker;
	}

	/** Registered alongside the dirty checker so prompts can list what changed. */
	setUnsavedChangesSummaryProvider(provider: (() => string[]) | null): void {
		this.unsavedChangesSummaryProvider = provider;
	}

	isOpenAndDirty(): boolean {
		return this.isOpen && !!this.dirtyChecker && this.dirtyChecker();
	}

	getUnsavedChangesSummary(): string[] {
		return this.unsavedChangesSummaryProvider?.() ?? [];
	}
}
